import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;

/** Set on `/signup` before Google OAuth so return via `/templates` can attribute signup in `setUserWrap`. */
export const MIXPANEL_OAUTH_FROM_SIGNUP_KEY = 'postdrop_mixpanel_oauth_from_signup';

/** Only attribute OAuth return to signup if the intent was started within this window. */
export const MIXPANEL_OAUTH_FROM_SIGNUP_TTL_MS = 10 * 60 * 1000;

let isInitialized = false;

/** Call before Google OAuth on `/signup`; value includes timestamp for TTL in `consumeMixpanelOAuthSignupIfPending`. */
export function setMixpanelOAuthSignupPending(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(
      MIXPANEL_OAUTH_FROM_SIGNUP_KEY,
      JSON.stringify({ v: 1, ts: Date.now() })
    );
  } catch {
    /* sessionStorage may be unavailable */
  }
}

export function clearMixpanelOAuthSignupPending(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.removeItem(MIXPANEL_OAUTH_FROM_SIGNUP_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * If signup-page Google OAuth was started recently, consume the intent and return true so
 * `setUserWrap` can emit signup events. Removes stale or legacy values without tracking.
 */
export function consumeMixpanelOAuthSignupIfPending(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const raw = sessionStorage.getItem(MIXPANEL_OAUTH_FROM_SIGNUP_KEY);
    if (raw == null) {
      return false;
    }
    sessionStorage.removeItem(MIXPANEL_OAUTH_FROM_SIGNUP_KEY);
    if (raw === '1') {
      return false;
    }
    const parsed = JSON.parse(raw) as { v?: number; ts?: number };
    if (parsed?.v === 1 && typeof parsed.ts === 'number') {
      return Date.now() - parsed.ts <= MIXPANEL_OAUTH_FROM_SIGNUP_TTL_MS;
    }
    return false;
  } catch {
    return false;
  }
}

export const initMixpanel = () => {
  if (isInitialized || typeof window === 'undefined') {
    return;
  }

  if (!MIXPANEL_TOKEN) {
    return;
  }

  try {
    mixpanel.init(MIXPANEL_TOKEN, {
      autocapture: false,
      record_sessions_percent: 0,
      ignore_dnt: true, // Respect Do Not Track
    });

    isInitialized = true;
  } catch (error) {
    console.error('[Mixpanel] Failed to initialize:', error);
  }
};

const applyMixpanelIdentify = (
  userId: string,
  traits: Record<string, any>
) => {
  mixpanel.identify(userId);
  if (Object.keys(traits).length > 0) {
    mixpanel.people.set(traits);
  }
};

export const identifyMixpanelUser = (
  userId: string,
  traits: Record<string, any> = {}
) => {
  if (!userId) {
    return;
  }
  if (typeof window !== 'undefined' && !isInitialized) {
    initMixpanel();
  }
  if (!isInitialized) {
    if (typeof window !== 'undefined') {
      setTimeout(() => {
        initMixpanel();
        if (isInitialized) {
          try {
            applyMixpanelIdentify(userId, traits);
          } catch (error) {
            console.error('[Mixpanel] Failed to identify user:', error);
          }
        } else {
          console.warn('[Mixpanel] Not initialized, identify skipped:', userId);
        }
      }, 200);
    }
    return;
  }

  try {
    applyMixpanelIdentify(userId, traits);
  } catch (error) {
    console.error('[Mixpanel] Failed to identify user:', error);
  }
};

/** Update Mixpanel people properties for the currently identified user. */
export const setMixpanelPeopleProperties = (
  traits: Record<string, any>
) => {
  if (!isInitialized || !traits || Object.keys(traits).length === 0) {
    return;
  }
  try {
    mixpanel.people.set(traits);
  } catch (error) {
    console.error('[Mixpanel] Failed to set people properties:', error);
  }
};

export const resetMixpanel = () => {
  if (!isInitialized) {
    return;
  }

  mixpanel.reset();
};

export const trackMixpanelEvent = (
  eventName: string,
  eventProperties: Record<string, any> = {}
) => {
  if (!isInitialized) {
    // If not initialized yet, try to initialize and retry
    if (typeof window !== 'undefined') {
      initMixpanel();
      // Retry after a short delay
      setTimeout(() => {
        if (isInitialized) {
          try {
            mixpanel.track(eventName, eventProperties);
          } catch (error) {
            console.error('[Mixpanel] Failed to track event:', error);
          }
        } else {
          // If still not initialized after retry, log warning
          console.warn('[Mixpanel] Not initialized, event not tracked:', eventName);
        }
      }, 200);
    }
    return;
  }

  try {
    mixpanel.track(eventName, eventProperties);
  } catch (error) {
    console.error('[Mixpanel] Failed to track event:', error);
  }
};

export default mixpanel;

